import { useContext, useEffect, useState } from 'react';
import { Variant } from 'shared/types/product';
import { Button } from 'components/ui/button';
import { useCart, useProduct } from 'frontastic/hooks';
import toast from 'react-hot-toast';
import { useMiniCart } from 'context/miniCart';
import QuantityInput from 'components/commercetools-ui/organisms/quantity-input';
import CrossSellModal from 'components/commercetools-ui/organisms/cross-sell-modal';
import { Product, ProductCardProduct } from 'shared/types/product/Product';
import { filterVariantsInSupplies, getMoreCompatible, getTitle } from 'helpers/utils/crossSellText';
import { gtmAddToCart, gtmQuantitySelector } from 'helpers/utils/gtm-events/gtmEccomerce';
import { LineItemMapper } from 'helpers/utils/gtm-events/mapper/LineItemMapper';
import { AccessorySelection } from 'shared/types/product/Variant';
import { AccountContext } from 'context/account';
import { isAValidDiscontinuedVariant } from 'helpers/utils/cart/cartValidators';
interface Props {
  variant?: Variant;
  onChangeQuantity?: (quantity: number) => void;
  product?: Product;
  selectedAccessories?: AccessorySelection;
  onAddToCart?: () => void;
}

const ProductQuantitySelector = ({ variant, product, onChangeQuantity, selectedAccessories, onAddToCart }: Props) => {
  const [quantity, setQuantity] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCrossSellOpen, setIsCrossSellOpen] = useState(false);
  const [supplies, setSupplies] = useState<ProductCardProduct[]>([]);
  const { queryProductSupplies } = useProduct();
  const { data: cart, addItems } = useCart();
  const { account } = useContext(AccountContext);

  useEffect(() => {
    // Restart the quantity value when variant changed
    setQuantity(1);
  }, [variant]);

  useEffect(() => {
    if (onChangeQuantity) {
      onChangeQuantity(quantity);
    }
  }, [quantity]);

  const isClearance = variant?.originalPrice && variant.price?.centAmount !== variant.originalPrice.centAmount;
  const isDiscontinued = variant?.attributes?.isDiscontinued ?? false;
  const isAnInvalidDiscontinuedVariant =
    cart &&
    variant &&
    isDiscontinued &&
    !isAValidDiscontinuedVariant({
      availableQuantity: variant.availableQuantity ?? 0,
      cart: cart,
      quantityToAdd: quantity,
      variantSku: variant.sku,
    });
  const increaseQuantity = () => {
    if (quantity <= (isClearance || isDiscontinued ? variant?.availableQuantity || 1 : 999)) {
      setQuantity((prev) => prev + 1);
      if (product && variant) {
        gtmQuantitySelector({
          clickAction: 'increased',
          newQuantity: quantity + 1,
          source: 'pdp',
          itemDataLayer: LineItemMapper.fromProductToItemDataLayer(product, quantity + 1, variant),
          cart,
          account,
        });
      }
    }
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
      if (product && variant) {
        gtmQuantitySelector({
          clickAction: 'reduced',
          newQuantity: quantity - 1,
          source: 'pdp',
          itemDataLayer: LineItemMapper.fromProductToItemDataLayer(product, quantity - 1, variant),
          cart,
          account,
        });
      }
    }
  };

  const { show: showMiniCart } = useMiniCart();

  const handleAddToCart = async () => {
    const itemsToAdd: { product?: Product; variant: Variant; quantity: number }[] = [];
    let addVariantToCart = true;
    setIsLoading(true);
    try {
      if (isDiscontinued && !variant?.availableQuantity) {
        throw new Error('No available quantity for discontinued variant');
      }

      if (isAnInvalidDiscontinuedVariant) {
        return;
      }

      if (selectedAccessories?.accessories && selectedAccessories.accessories.length > 0) {
        if (selectedAccessories.accessoriesOnly) {
          addVariantToCart = false;
        }
        selectedAccessories.accessories.forEach((accessory) => {
          itemsToAdd.push({
            product: accessory.product,
            variant: accessory.variant,
            quantity: accessory.quantity || 0,
          });
        });
      }
      if (variant && addVariantToCart) {
        itemsToAdd.push({
          product: product,
          variant: variant,
          quantity: quantity || 0,
        });
      }

      await addItems(itemsToAdd);

      itemsToAdd.forEach((item) => {
        gtmAddToCart({
          itemDataLayer: LineItemMapper.fromProductToItemDataLayer(item.product!, item.quantity, item.variant),
          quantity: item.quantity,
          cart,
          account,
        });
      });

      toast.success('Item Added');
      onAddToCart?.();

      const supplies = variant?.attributes?.supplies;

      if (supplies?.length) {
        try {
          const suppliesData = await queryProductSupplies(supplies);
          const suppliesFiltered = filterVariantsInSupplies(suppliesData, variant?.attributes?.size);
          if (suppliesFiltered.length > 0) {
            setSupplies(suppliesFiltered);
            setIsCrossSellOpen(true);
          } else {
            showMiniCart();
          }
        } catch (e) {
          showMiniCart();
        }
      } else {
        showMiniCart();
      }
    } catch (error) {
      toast.error('There was an error!');
    } finally {
      setIsLoading(false);
    }
  };

  const isAddToCartDisabled =
    isLoading || (isDiscontinued && !variant?.availableQuantity) || isAnInvalidDiscontinuedVariant;

  return (
    <section className="mb-6 flex items-center justify-end gap-[10px]">
      <label className="text-sm font-semibold text-primary-dark" htmlFor="itemQuantity" aria-label="Quantity">
        Qty
      </label>
      <section className="relative flex h-11 w-[6.25rem] items-center justify-between rounded-lg border lg:w-[80px]">
        <QuantityInput
          maxQuantity={isClearance || isDiscontinued ? variant?.availableQuantity || 1 : 999}
          onChange={setQuantity}
          onDecreaseQuantity={decreaseQuantity}
          onIncreaseQuantity={increaseQuantity}
          quantity={quantity}
        />
      </section>
      <Button
        aria-label="Add to cart"
        disabled={isAddToCartDisabled}
        className={
          'mb-0 h-11 w-full cursor-pointer rounded-[6px] bg-[#404040] text-sm font-semibold text-white hover:bg-[#333] disabled:cursor-not-allowed disabled:opacity-30 sm:w-[233px]'
        }
        onClick={handleAddToCart}
        id="addToCartBtn"
      >
        Add to cart
      </Button>
      {supplies.length > 0 && variant && (
        <CrossSellModal
          isOpen={isCrossSellOpen}
          onClose={() => setIsCrossSellOpen(false)}
          supplies={supplies}
          minRequired={variant?.attributes?.numberOfLights || 1}
          title={getTitle(supplies)}
          moreCompatible={getMoreCompatible(variant, supplies)}
        />
      )}
    </section>
  );
};

export default ProductQuantitySelector;
